// Phase 43.19 — Centro Operativo Admin (8 card actionable + countdown card)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, AlertTriangle, Banknote, Clock, Truck,
  UserCheck, Bell, FileWarning, MailCheck, ChevronRight,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatDateTime, STATUS_LABEL } from "@/lib/format";

const REFRESH_MS = 30_000; // auto-refresh 30s

export default function AdminOps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/ops/summary");
      setData(data);
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const resendLink = async (orderId) => {
    setWorking(orderId);
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/resend-payment-link`);
      if (data.ok) {
        toast.success("Link di pagamento rigenerato. Verifica nei log l’esito delle notifiche al cliente.");
      } else {
        toast.error(`Impossibile reinviare: ${data.error || "errore"}`);
      }
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setWorking(null);
    }
  };

  const totalIssues = data
    ? data.payment_pending_long.count + data.orders_no_driver.count + data.orders_stalled.count +
      data.orders_overdue.count + data.refunds_pending.count + data.drivers_doc_expiring.count +
      data.drivers_to_verify.count + data.notif_errors_recent.count
    : 0;

  return (
    <div className="space-y-6" data-testid="admin-ops-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Admin · Centro Operativo</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">
            Cosa richiede intervento
          </h1>
          <p className="text-sm text-dapnow-muted mt-1">
            {data ? `${totalIssues} segnalazioni totali. Auto-refresh ogni 30s.` : "Caricamento…"}
          </p>
        </div>
        <button
          onClick={() => load(false)}
          disabled={loading}
          data-testid="admin-ops-reload"
          className="inline-flex items-center gap-2 rounded-lg border border-dapnow-line bg-white px-3 h-10 text-sm font-semibold hover:border-dapnow-blue disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {!data && loading && (
        <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento dati operativi…</div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* 1. Payment pending > 30 min */}
          <OpsCard
            testId="ops-card-payment-pending"
            icon={<Clock className="h-5 w-5" />}
            kind="warn"
            title="Pagamenti pending > 30 min"
            count={data.payment_pending_long.count}
            description={`Clienti che hanno creato l'ordine ma non hanno completato il pagamento Stripe.`}
            emptyMsg="Nessun ordine pending payment."
            actionLink={data.payment_pending_long.count > 0 ? { to: "/admin/orders?filter=pending_payment", label: "Vedi lista filtrata" } : null}
          >
            {data.payment_pending_long.items.map((o) => (
              <OrderRow key={o.id} order={o}>
                <button
                  onClick={() => resendLink(o.id)}
                  disabled={working === o.id}
                  data-testid={`ops-resend-link-${o.id}`}
                  className="ops-action-btn"
                >
                  <MailCheck className="h-3.5 w-3.5" />
                  {working === o.id ? "Invio…" : "Reinvia link"}
                </button>
              </OrderRow>
            ))}
          </OpsCard>

          {/* 2. Refunds pending */}
          <OpsCard
            testId="ops-card-refunds"
            icon={<Banknote className="h-5 w-5" />}
            kind="warn"
            title="Rimborsi da processare"
            count={data.refunds_pending.count}
            description="Ordini cancellati pagati con refund ancora da emettere via Stripe."
            emptyMsg="Nessun rimborso in attesa registrato."
            actionLink={data.refunds_pending.count > 0 ? { to: "/admin/refunds?filter=pending", label: "Apri Centro Rimborsi" } : null}
          >
            {data.refunds_pending.items.slice(0, 3).map((o) => (
              <OrderRow key={o.id} order={o}>
                <span className="text-xs font-semibold text-amber-700">
                  {o.refund_status === "failed" ? "Refund failed" : formatCHF(o.refund_amount || (o.price || 0))}
                </span>
              </OrderRow>
            ))}
          </OpsCard>

          {/* 3. Orders no driver */}
          <OpsCard
            testId="ops-card-no-driver"
            icon={<Truck className="h-5 w-5" />}
            kind="info"
            title="Ordini pagati senza driver"
            count={data.orders_no_driver.count}
            description={`Paid ma in coda da più di ${data.thresholds.no_driver_minutes} min.`}
            emptyMsg="Nessun ordine pagato senza driver da oltre 15 minuti."
            actionLink={data.orders_no_driver.count > 0 ? { to: "/admin/orders?filter=no_driver", label: "Vedi lista filtrata" } : null}
          >
            {data.orders_no_driver.items.map((o) => (
              <OrderRow key={o.id} order={o}>
                <Link to={`/admin/orders?filter=no_driver&focus=${o.id}`} className="ops-action-btn-secondary">
                  Apri <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </OrderRow>
            ))}
          </OpsCard>

          {/* 4. Stalled */}
          <OpsCard
            testId="ops-card-stalled"
            icon={<AlertTriangle className="h-5 w-5" />}
            kind="warn"
            title="Ordini fermi (driver inattivo)"
            count={data.orders_stalled.count}
            description={`Accettati da > ${data.thresholds.stall_minutes} min senza ritiro.`}
            emptyMsg="Nessun ordine accettato fermo oltre la soglia monitorata."
            actionLink={data.orders_stalled.count > 0 ? { to: "/admin/orders?filter=stale", label: "Vedi lista filtrata" } : null}
          >
            {data.orders_stalled.items.map((o) => (
              <OrderRow key={o.id} order={o}>
                <Link to={`/admin/orders?filter=stale&focus=${o.id}`} className="ops-action-btn-secondary">
                  Apri <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </OrderRow>
            ))}
          </OpsCard>

          {/* 5. Overdue */}
          <OpsCard
            testId="ops-card-overdue"
            icon={<AlertTriangle className="h-5 w-5" />}
            kind="danger"
            title="Ordini in ritardo"
            count={data.orders_overdue.count}
            description={`In consegna da > ${data.thresholds.overdue_minutes} min senza chiusura.`}
            emptyMsg="Nessun ordine in consegna da oltre 90 minuti."
            actionLink={data.orders_overdue.count > 0 ? { to: "/admin/orders?filter=late", label: "Vedi lista filtrata" } : null}
          >
            {data.orders_overdue.items.map((o) => (
              <OrderRow key={o.id} order={o}>
                <Link to={`/admin/orders?filter=late&focus=${o.id}`} className="ops-action-btn-secondary">
                  Apri <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </OrderRow>
            ))}
          </OpsCard>

          {/* 6. Driver doc expiring */}
          <OpsCard
            testId="ops-card-driver-docs"
            icon={<FileWarning className="h-5 w-5" />}
            kind={data.drivers_doc_expiring.count > 0 ? "warn" : "ok"}
            title="Driver doc in scadenza"
            count={data.drivers_doc_expiring.count}
            description={`Patente o assicurazione entro ${data.thresholds.doc_expiry_days} giorni.`}
            emptyMsg="Nessuna scadenza documentale segnalata nei prossimi 30 giorni."
            actionLink={data.drivers_doc_expiring.count > 0 ? { to: "/admin/drivers?filter=compliance_expired", label: "Vedi lista filtrata" } : null}
          >
            {data.drivers_doc_expiring.items.map((d) => (
              <DriverRow key={d.id} driver={d} today={data.drivers_doc_expiring.today} />
            ))}
          </OpsCard>

          {/* 7. Driver to verify */}
          <OpsCard
            testId="ops-card-driver-verify"
            icon={<UserCheck className="h-5 w-5" />}
            kind="info"
            title="Driver da verificare"
            count={data.drivers_to_verify.count}
            description="KYC pending — compliance_verified_at mancante."
            emptyMsg="Nessun driver in attesa di verifica."
            actionLink={data.drivers_to_verify.count > 0 ? { to: "/admin/drivers?filter=verification_pending", label: "Vedi lista filtrata" } : null}
          >
            {data.drivers_to_verify.items.map((d) => (
              <DriverRow key={d.id} driver={d} />
            ))}
          </OpsCard>

          {/* 8. Notification errors */}
          <OpsCard
            testId="ops-card-notif-errors"
            icon={<Bell className="h-5 w-5" />}
            kind={data.notif_errors_recent.count > 0 ? "warn" : "ok"}
            title="Errori notifiche (24h)"
            count={data.notif_errors_recent.count}
            description="WhatsApp/Email/SMS fallite nelle ultime 24h."
            emptyMsg="Nessun errore di notifica registrato nelle ultime 24 ore. Verifica separatamente che i canali siano attivi."
          >
            {data.notif_errors_recent.items.map((n) => (
              <div key={n.id} className="text-xs border-t border-dapnow-line py-2 first:border-t-0">
                <p className="font-semibold">{n.channel} · {n.event_type}</p>
                <p className="text-dapnow-muted truncate" title={n.error}>
                  → {n.recipient} · {n.error || "—"}
                </p>
                <p className="text-[10px] text-dapnow-muted">{formatDateTime(n.created_at)}</p>
              </div>
            ))}
          </OpsCard>
        </div>
      )}
    </div>
  );
}

function OpsCard({ testId, icon, kind, title, count, description, emptyMsg, children, actionLink }) {
  const palette = {
    info:   "border-l-blue-400 bg-white",
    warn:   "border-l-amber-400 bg-white",
    danger: "border-l-rose-500 bg-rose-50/30",
    ok:     "border-l-emerald-400 bg-white",
  }[kind] || "border-l-zinc-400 bg-white";
  const iconBg = {
    info:   "bg-blue-100 text-blue-700",
    warn:   "bg-amber-100 text-amber-800",
    danger: "bg-rose-100 text-rose-700",
    ok:     "bg-emerald-100 text-emerald-700",
  }[kind] || "bg-zinc-100 text-zinc-700";

  return (
    <section className={`card-soft border-l-4 ${palette}`} data-testid={testId}>
      <div className="p-5 border-b border-dapnow-line">
        <div className="flex items-start gap-3">
          <span className={`inline-flex h-10 w-10 rounded-xl items-center justify-center ${iconBg}`}>
            {icon}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
              <span data-testid={`${testId}-count`} className={`text-2xl font-display font-extrabold tabular-nums ${count > 0 ? "text-dapnow-ink" : "text-dapnow-muted"}`}>
                {count}
              </span>
            </div>
            <p className="text-xs text-dapnow-muted mt-1">{description}</p>
          </div>
        </div>
      </div>
      <div className="p-3">
        {count === 0 ? (
          <p className="text-xs text-dapnow-muted text-center py-3">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5 align-middle" />
            {emptyMsg}
          </p>
        ) : (
          <div className="space-y-0 max-h-72 overflow-y-auto">
            {children}
            {actionLink && (
              <Link
                to={actionLink.to}
                className="block text-center text-xs font-semibold text-dapnow-blue hover:underline mt-2 py-2"
              >
                {actionLink.label} →
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function OrderRow({ order, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-t border-dapnow-line first:border-t-0">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-mono text-dapnow-muted">#{order.id.slice(0, 8).toUpperCase()}</p>
        <p className="text-sm font-semibold truncate">{order.customer_name || "—"}</p>
        <p className="text-xs text-dapnow-muted truncate">
          {STATUS_LABEL[order.status]} · {formatCHF(order.price)} · {formatDateTime(order.created_at)}
        </p>
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function DriverRow({ driver, today }) {
  const lic = driver.license_expiry;
  const ins = driver.insurance_expiry;
  const isExpired = (d) => d && today && d < today;
  const verified = !!driver.compliance_verified_at;
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-t border-dapnow-line first:border-t-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{driver.name || driver.email}</p>
        <div className="text-[11px] text-dapnow-muted flex flex-wrap gap-x-3">
          {lic && (
            <span className={isExpired(lic) ? "text-rose-600 font-semibold" : ""}>
              Patente: {lic}
            </span>
          )}
          {ins && (
            <span className={isExpired(ins) ? "text-rose-600 font-semibold" : ""}>
              Assic.: {ins}
            </span>
          )}
          {!verified && <span className="text-amber-700 font-semibold">KYC pending</span>}
        </div>
      </div>
      <Link
        to={`/admin/drivers/${driver.id}`}
        className="ops-action-btn-secondary"
        data-testid={`ops-open-driver-${driver.id}`}
      >
        Apri <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
